import { Component, input, model, signal, output, inject, OnDestroy, computed } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { combineLatest, distinctUntilKeyChanged, map, switchMap, tap } from 'rxjs';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiV1_AvailableIntegration, ApiV1_Establishment } from '@pedix-workspace/api-v1';
import { DeliveryService } from '@pedix-workspace/pedixapp-core-services';
import {
  TypesOfShippings,
  ShippingEstimate,
  EstablishmentCheckout,
  Address,
  CartItem,
  CorreoArgentinoAgency,
} from '@pedix-workspace/utils';
import {
  isShippingCostResponseDelivery,
  isShippingCostResponseParcel,
  ShippingCostResponse,
} from '@pedix-workspace/shared-delivery';
import { ShippingCalculationComponent } from './shipping-calculation/shipping-calculation.component';
import { ShippingCostResponseDeliveryComponent } from './shipping-cost-response-delivery/shipping-cost-response-delivery.component';
import { ShippingCostResponseParcelComponent } from './shipping-cost-response-parcel/shipping-cost-response-parcel.component';

@Component({
  selector: 'pxw-shipping-cost-response',
  templateUrl: './shipping-cost-response.component.html',
  styleUrls: ['./shipping-cost-response.component.scss'],
  imports: [
    TranslocoDirective,
    FormsModule,
    ShippingCalculationComponent,
    ShippingCostResponseDeliveryComponent,
    ShippingCostResponseParcelComponent,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class ShippingCostResponseComponent implements OnDestroy {
  private readonly t = inject(TranslocoService);
  readonly deliveryService = inject(DeliveryService);

  readonly isFormSubmitted = input.required<boolean>();
  readonly apiUrl = input.required<string>();
  readonly establishment = input.required<ApiV1_Establishment>();

  readonly shippingCostResponse = model.required<ShippingCostResponse | null>();
  readonly calculatingShippingCost = model.required<boolean>();

  readonly address = input<Address | null>(null);
  readonly name = input<string | null | undefined>(null);
  readonly phone = input<string | null | undefined>(null);
  readonly cartItems = input<CartItem[]>([]);
  readonly totalAmountWithDiscount = input<number>(0);
  readonly previousShippingEstimate = input<ShippingEstimate | null>(null);
  readonly availableLogisticIntegrations = input<ApiV1_AvailableIntegration[]>([]);
  readonly selectedParcelOption = input<ShippingEstimate | null>(null);
  readonly checkoutData = input<EstablishmentCheckout | null>(null);

  readonly outOfDeliveryZoneConfirmed = signal<boolean>(false);
  readonly selectedBranch = signal<CorreoArgentinoAgency | null>(null);
  readonly shippingCalculationError = signal<string | null>(null);

  readonly userCoordinates = computed(() => this.address()?.addressDetails?.coordinates || null);

  readonly selectedParcelOptionChange = output<ShippingEstimate | null>();
  readonly selectedBranchChange = output<CorreoArgentinoAgency | null>();

  protected typesOfShippings = TypesOfShippings;
  protected isFirstTimeCalculatingShippingCost = true;

  protected readonly isShippingCostResponseDelivery = isShippingCostResponseDelivery;
  protected readonly isShippingCostResponseParcel = isShippingCostResponseParcel;

  constructor() {
    combineLatest({
      establishment: toObservable(this.establishment),
      checkoutData: toObservable(this.checkoutData),
      address: toObservable(this.address).pipe(
        map(address => address || {}),
        distinctUntilKeyChanged('addressDetails'),
      ),
    })
      .pipe(
        tap(() => {
          this.calculatingShippingCost.set(true);
          this.shippingCalculationError.set(null);
        }),
        switchMap(async ({ establishment, checkoutData }) => {
          return this.deliveryService
            .calculateShippingCost({
              apiBaseUrl: this.apiUrl(),
              shippingOptionDelivery: checkoutData?.shippingOptionDelivery,
              shippingOptionParcel: checkoutData?.shippingOptionParcel,
              address: this.address(),
              name: this.name() || null,
              phone: this.phone() || null,
              hasLogisticsIntegration: this.availableLogisticIntegrations().length > 0,
              establishment,
              cartItems: this.cartItems(),
              totalAmountWithDiscount: this.totalAmountWithDiscount(),
              previousShippingEstimate: this.isFirstTimeCalculatingShippingCost
                ? this.previousShippingEstimate()
                : null,
              selectedParcelOption: this.selectedParcelOption(),
            })
            .catch(error => {
              console.error('Error calculating shipping cost:', error);
              this.shippingCalculationError.set(this.t.translate('shippingCostResponse.coverageUnknownError'));
              return null;
            });
        }),
        takeUntilDestroyed(),
      )
      .subscribe(shippingCostResponse => {
        this.shippingCostResponse.set(shippingCostResponse);

        this.calculatingShippingCost.set(false);

        this.isFirstTimeCalculatingShippingCost = false;
      });
  }

  onParcelOptionSelected(option: ShippingEstimate | null) {
    this.selectedParcelOptionChange.emit(option);
  }

  onBranchSelected(branch: CorreoArgentinoAgency | null) {
    this.selectedBranch.set(branch);
    this.selectedBranchChange.emit(branch);
  }

  ngOnDestroy() {
    this.shippingCostResponse.set(null);
  }
}
